/**
 * Shared safe-next path helper used by every post-auth redirect
 * surface (Login, Signup, AuthCallback).
 *
 * A single allow-list of internal relative destinations prevents any
 * `?next=` value from becoming an open redirect vector.  Nothing here
 * ever accepts a scheme, protocol-relative URL (`//evil.com`), or a
 * backslash-smuggled path.
 *
 * When called with an unknown/unsafe value we fall back to
 * `/watchlist` — the same default the pre-refactor Login and Signup
 * pages used.  Callers that want a different default can pass one via
 * the second argument.
 */
export const POST_AUTH_NEXT_ALLOWLIST = [
  "/watchlist",
  "/portfolio",
  "/billing",
  "/billing/continue-checkout",
  "/pricing",
  "/account",
  "/analyze",
  "/owner-console",
];

export function safeNextPath(raw, fallback = "/watchlist") {
  if (!raw || typeof raw !== "string") return fallback;
  // Must be internal relative — no schemes, no protocol-relative URLs.
  if (!raw.startsWith("/") || raw.startsWith("//")) return fallback;
  if (raw.includes("\\")) return fallback;
  const base = raw.split("?")[0].split("#")[0];
  for (const allowed of POST_AUTH_NEXT_ALLOWLIST) {
    if (base === allowed || base.startsWith(allowed + "/")) return raw;
  }
  return fallback;
}

/**
 * Stash the intended post-Google-auth destination in sessionStorage
 * so the Google callback (which cannot carry a `?next=` through the
 * OAuth redirect) can consume it after the session is established.
 *
 * Only stores validated internal paths.  Silently no-ops if
 * sessionStorage is unavailable.
 */
const STASH_KEY = "__stockaily_return_after_google";

export function stashGoogleReturnPath(next) {
  const safe = safeNextPath(next, null);
  if (!safe) return;
  try {
    if (typeof window !== "undefined" && window.sessionStorage) {
      window.sessionStorage.setItem(STASH_KEY, safe);
    }
  } catch {
    /* sessionStorage disabled — safe to ignore */
  }
}

/** One-shot read + clear of the stashed Google-return destination. */
export function consumeGoogleReturnPath() {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return null;
    const raw = window.sessionStorage.getItem(STASH_KEY);
    window.sessionStorage.removeItem(STASH_KEY);
    return safeNextPath(raw, null);
  } catch {
    return null;
  }
}
